/**
 * RequireCapability — route guard that hides a page behind a backend
 * capability flag from `/api/auth/permissions`.
 *
 * Usage:
 *   <RequireCapability cap="reports"><Reports /></RequireCapability>
 *
 * Semantics:
 *   • Capability map still loading  → render a neutral loading spinner
 *     (keeps behavior consistent with the existing ProtectedRoute).
 *   • Capability truthy             → render children.
 *   • Capability false OR fetch err → render an "Access denied" state with
 *     a Dashboard link.  Same restrictive-fallback contract as the sidebar.
 */
import { Link } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { useAuth } from "@/App";
import { useCan } from "@/lib/capabilities";

export default function RequireCapability({ cap, children }) {
  const { permissions, permsError, loading } = useAuth() || {};
  const allowed = useCan(cap);

  if (loading || (!permissions && !permsError)) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
           data-testid="capability-loading">
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 22, height: 22, border: "2.5px solid var(--blue)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 8px" }} />
          <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Checking access…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (allowed) return children;

  return (
    <div data-testid="access-denied"
         style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <div style={{ maxWidth: 380, textAlign: "center" }}>
        <div style={{ width: 48, height: 48, margin: "0 auto 14px", borderRadius: "50%",
                      background: "#FCEBEB", display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#A32D2D" }}>
          <ShieldOff style={{ width: 22, height: 22 }} />
        </div>
        <h2 style={{ fontSize: 17, fontWeight: 650, marginBottom: 6 }}>Access denied</h2>
        <p style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 18, lineHeight: 1.5 }}>
          You don't have permission to view this page. If you believe this is a mistake,
          please contact your administrator.
        </p>
        <Link to="/" data-testid="access-denied-dashboard-link"
              style={{ display: "inline-block", background: "#2563eb", color: "#fff",
                       padding: "8px 18px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                       textDecoration: "none" }}>
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}
